import { get as getCookie } from 'js-cookie';

import { getCurrentLanguage } from '~/localization';

import { baseFetchFactory } from './baseFetchFactory';
import { baseProcessRequestInfoFactory } from './baseProcessRequestInfoFactory';
import * as IAm from './client.iam.generated';
import * as People from './client.people.generated';
import * as Rewarding from './client.rewarding.generated';

export * from './models.iam';
export * from './models.old';
export * from './models.people';

export { IAm, People };

const handleFetchResponseValidity = async (
  response: Response,
): Promise<Response> => {
  if (response.ok) {
    return response;
  }

  throw response;
};

export const fetchFromOldApi = baseFetchFactory(
  baseProcessRequestInfoFactory(`${process.env.REACT_APP_OLD_API_HOSTNAME}/d`),
  (init?: RequestInit) => ({
    ...init,
    headers: {
      ...init?.headers,
      'X-Solaforce-Token': getCookie('SOLAFORCE1') as string,
      Language: getCurrentLanguage(),
    },
  }),
  handleFetchResponseValidity,
);

export const basicFileDownload = (
  urlForApiCall: string,
  attachmentName: string,
) => {
  interface msSaveOrOpenBlob extends Navigator {
    msSaveOrOpenBlob?: (blob: object, attacmentName: string) => void;
  }
  fetchFromOldApi(urlForApiCall, { headers: { responseType: 'blob' } })
    .then((r) => r.blob())
    .then((response) => {
      // IE support
      const newNavigator = window.navigator as msSaveOrOpenBlob;
      if (newNavigator.msSaveOrOpenBlob) {
        const blob: object = new Blob([response], {
          type: 'text/calendar;charset=utf-8',
        });
        newNavigator.msSaveOrOpenBlob(blob, attachmentName);
        return;
      }
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachmentName);
      link.click();
    });
};

export const fetchFromNewApi = baseFetchFactory(
  baseProcessRequestInfoFactory(process.env.REACT_APP_API_HOSTNAME),
  undefined,
  handleFetchResponseValidity,
);

const clientFetcher = baseFetchFactory(
  baseProcessRequestInfoFactory(process.env.REACT_APP_API_HOSTNAME),
);

export const AuthenticationApiClient = new IAm.AuthenticationControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const PreferencesApiClient = new IAm.PreferencesControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const CompanyPreferencesApiClient =
  new IAm.CompanyPreferencesControllerClient(undefined, {
    fetch: clientFetcher,
  });

export const WorkflowConfigApiClient = new IAm.WorkflowConfigControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const ProposalsConfigApiClient =
  new IAm.ProposalTrackingControllerClient(undefined, { fetch: clientFetcher });

export const UserActivityApiClient = new IAm.UserActivityControllerClient(
  undefined,
  {
    fetch: clientFetcher,
  },
);

export const AbsenceReadApiClient = new People.AbsenceReadControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const AnniversaryReadApiClient =
  new People.AnniversaryReadControllerClient(undefined, {
    fetch: clientFetcher,
  });

export const AnnouncementApiClient = new People.AnnouncementControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const CompanyPostApiClient = new People.CompanyPostControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const EmployeeReadApiClient = new People.EmployeeReadControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const PublicHolidayApiClient = new People.PublicHolidayControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const NotificationReadApiClient =
  new People.NotificationReadControllerClient(undefined, {
    fetch: clientFetcher,
  });

export const QuickLinkApiClient = new People.QuickLinkControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const TaskReadApiClient = new People.TaskReadControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const TimesheetApiClient = new People.TimesheetControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const TrainingReadApiClient = new People.TrainingReadControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const RelationsReadApiClient = new People.RelationsReadControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const AllowanceProposalsReadApiClient =
  new Rewarding.AllowanceControllerClient(undefined, { fetch: clientFetcher });

export const BenefitProposalsReadApiClient =
  new Rewarding.BenefitControllerClient(undefined, { fetch: clientFetcher });

export const IncentiveProposalsReadApiClient =
  new Rewarding.IncentiveControllerClient(undefined, { fetch: clientFetcher });

export const SalaryProposalsReadApiClient =
  new Rewarding.SalaryControllerClient(undefined, { fetch: clientFetcher });

export const RewardingPostApiClient = new Rewarding.RewardControllerClient(
  undefined,
  { fetch: clientFetcher },
);

export const CompensationReadApiClient =
  new People.CompensationReadControllerClient(undefined, {
    fetch: clientFetcher,
  });

export const PerformanceGoalControllerClient =
  new People.PerformanceGoalControllerClient(undefined, {
    fetch: clientFetcher,
  });

export const EvaluationControllerClient = new People.EvaluationControllerClient(
  undefined,
  {
    fetch: clientFetcher,
  },
);
